import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { PdfUploader } from "./PdfUploader";
import { ExtractedComponentsReview } from "./ExtractedComponentsReview";
import { Button } from "@/components/ui/button";
import { TripComponent, InsertTripComponent } from "@shared/schema";
import { useToast } from "@/hooks/use-toast";
import { useTripComponents } from "@/hooks/useTripComponents";

export interface PdfImportDialogProps {
  tripId: number;
  trigger: React.ReactNode;
  onImportComplete?: () => void;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}

export function PdfImportDialog({ 
  tripId, 
  trigger, 
  onImportComplete, 
  open: controlledOpen, 
  onOpenChange 
}: PdfImportDialogProps) {
  const [internalOpen, setInternalOpen] = useState(false);
  const [extractedComponents, setExtractedComponents] = useState<TripComponent[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const [uploadStep, setUploadStep] = useState<"upload" | "review">("upload");
  const { toast } = useToast();
  const { createComponents } = useTripComponents(tripId);

  // Use controlled or uncontrolled open state
  const open = controlledOpen !== undefined ? controlledOpen : internalOpen;
  
  // Handler for open state changes
  const handleOpenChange = (newOpen: boolean) => {
    if (onOpenChange) {
      onOpenChange(newOpen);
    } else {
      setInternalOpen(newOpen);
    }
  };

  const handlePdfProcessed = (components: TripComponent[]) => {
    // Make sure components is always an array
    const validComponents = Array.isArray(components) ? components : [];
    setExtractedComponents(validComponents);
    setUploadStep("review");
    setIsUploading(false);
  };

  const handleCancel = () => {
    handleOpenChange(false);
    setExtractedComponents([]);
    setUploadStep("upload");
  };

  const handleImport = async (selectedComponents: TripComponent[]) => {
    if (selectedComponents.length === 0) {
      toast({
        title: "No components selected",
        description: "Please select at least one component to import",
        variant: "destructive",
      });
      return;
    }

    try {
      setIsImporting(true);

      // Convert TripComponent to InsertTripComponent by mapping to ensure type correctness
      const insertComponents = selectedComponents.map(component => ({
        tripId: component.tripId,
        type: component.type,
        title: component.title,
        description: component.description,
        location: component.location,
        details: component.details as Record<string, any> | null,
        dayIndex: component.dayIndex,
        startDate: component.startDate,
        endDate: component.endDate,
        startTime: component.startTime,
        endTime: component.endTime
      }));

      // Use the createComponents method from useTripComponents to create all components at once
      await createComponents(insertComponents);
      
      toast({
        title: "Success",
        description: `${selectedComponents.length} components imported successfully`,
      });

      // Close the dialog and reset state
      handleOpenChange(false);
      setExtractedComponents([]);
      setUploadStep("upload");
      
      // Call the onImportComplete callback if provided
      if (onImportComplete) {
        onImportComplete();
      }
    } catch (error) {
      console.error("Error importing components:", error);
      toast({
        title: "Import failed",
        description: "Failed to import components. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsImporting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent className="sm:max-w-[700px] max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle>
            {uploadStep === "upload" ? "Import Itinerary from PDF" : "Review Extracted Components"}
          </DialogTitle>
          <DialogDescription>
            {uploadStep === "upload"
              ? "Upload a travel package PDF to extract itinerary components automatically."
              : `We've extracted ${extractedComponents.length} components from your PDF. Review and select the ones you want to import.`}
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto">
          {uploadStep === "upload" ? (
            <PdfUploader
              tripId={tripId}
              onProcessingStart={() => setIsUploading(true)}
              onProcessed={handlePdfProcessed}
              onError={(error) => {
                toast({
                  title: "PDF Processing Error",
                  description: error || "Failed to process PDF. Please try again.",
                  variant: "destructive",
                });
                setIsUploading(false);
              }}
            />
          ) : (
            <ExtractedComponentsReview
              components={extractedComponents}
              onImport={handleImport}
              isImporting={isImporting}
            />
          )}
        </div>

        <div className="flex justify-between mt-4">
          <Button variant="outline" onClick={handleCancel} disabled={isUploading || isImporting}>
            Cancel
          </Button>
          
          {uploadStep === "review" && (
            <Button 
              onClick={() => setUploadStep("upload")} 
              variant="outline" 
              disabled={isImporting}
            >
              Back to Upload
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}